import React, { useRef, useEffect, useState } from 'react';
import VideoPlayer from './components/VideoPlayer';
import TranscriptBlocks from './components/TranscriptBlocks';
import Toolbar from './components/Toolbar';
import Sidebar from './components/Sidebar';
import ReplaceAllModal from './components/Modals/ReplaceAllModal';
import AssignToSpeakerModal from './components/Modals/AssignToSpeakerModal';
import useTranscriptState from './hooks/useTranscriptState';
import useVideoSync from './hooks/useVideoSync';
import useAutoSave from './hooks/useAutoSave';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
import useTextSelection from './hooks/useTextSelection';
import useLockManagement from './hooks/useLockManagement';
import { countOccurrences } from './utils/transcriptOperations';
import { CONFIG } from './constants/config';
import './TranscriptEditor.css';

/**
 * TranscriptEditor Component
 * 
 * Main transcript editing interface with video sync
 */
const TranscriptEditor = ({
  sessionId,
  videoUrl,
  initialBlocks = [],
  sessionTitle,
  backUrl,
  className = '',
}) => {
  const videoRef = useRef(null);
  const videoPlayerFullscreenRef = useRef(null);
  const transcriptScrollParentRef = useRef(null);
  const transcriptContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Replace All Modal state
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceSearchText, setReplaceSearchText] = useState('');
  // Assign to Speaker Modal state
  const [showAssignSpeakerModal, setShowAssignSpeakerModal] = useState(false);
  const capturedAssignDataRef = useRef(null);

  /**
   * Lock management
   */
  const {
    lockStatus,
    hasLock,
    lockLoading,
    lockInitializing,
    acquireLock: handleAcquireLock,
    releaseLock: handleReleaseLock,
    forceOverrideLock: handleForceOverride,
  } = useLockManagement(sessionId);

  /**
   * Transcript state management
   */
  const {
    blocks,
    selectedBlocks,
    focusedBlock,
    speakers,
    searchQuery,
    searchResults,
    currentSearchIndex,
    undo,
    redo,
    canUndo,
    canRedo,
    handleSplit,
    handleMergeUp,
    handleMergeDown,
    handleInsertBlock,
    handleDelete,
    handleDuplicate,
    handleUpdateText,
    handleUpdateSpeaker,
    handleBulkUpdateSpeaker,
    handleReorder,
    handleMoveUp,
    handleMoveDown,
    handleFocusBlock,
    handleBlurBlock,
    handleFocusNext,
    handleFocusPrevious,
    handleSelectBlock,
    handleSelectRange,
    handleClearSelection,
    handleSearch,
    handleNextSearch,
    handlePreviousSearch,
    handleReplace,
    handleAddSpeaker,
    handleRenameSpeaker,
    handleDeleteSpeaker,
    handleReplaceAll,
    captureSelectionForAssign,
    handleAssignSelectionToSpeaker,
  } = useTranscriptState(initialBlocks, sessionId);

  /**
   * Video sync
   */
  const {
    currentBlockId,
    currentTime,
    duration,
    isPlaying,
    seekTo,
    seekToBlock,
    togglePlay,
    play,
    pause,
    skip,
    setPlaybackRate,
  } = useVideoSync(blocks, videoRef, {
    autoScroll: false,
    scrollBehavior: 'smooth',
    scrollBlock: 'center',
  });

  /**
   * Auto-save
   */
  const {
    isSaving,
    lastSaved,
    saveError,
    isDirty,
    manualSave,
  } = useAutoSave(blocks, sessionId, CONFIG.AUTO_SAVE_DELAY, false);

  /**
   * Text selection for Replace All
   */
  const { selection, clearSelection } = useTextSelection(transcriptContainerRef);

  /**
   * Keyboard shortcuts
   */
  useKeyboardShortcuts(
    {
      onSplit: () => {
        if (focusedBlock) {
          // Cursor position will be handled by TranscriptBlock component
        }
      },
      onMergeUp: () => {
        if (focusedBlock) {
          handleMergeUp(focusedBlock);
        }
      },
      onMergeDown: () => {
        if (focusedBlock) {
          handleMergeDown(focusedBlock);
        }
      },
      onDelete: () => {
        if (focusedBlock) {
          handleDelete(focusedBlock);
        }
      },
      onDuplicate: () => {
        if (focusedBlock) {
          handleDuplicate(focusedBlock);
        }
      },
      onUndo: undo,
      onRedo: redo,
      onSave: manualSave,
      onSearch: () => {
        // Will be handled by Toolbar SearchBar
      },
      onPlayPause: togglePlay,
      onSkipForward: (seconds) => skip(seconds),
      onSkipBackward: (seconds) => skip(seconds),
      onNextBlock: handleFocusNext,
      onPreviousBlock: handleFocusPrevious,
      onMoveUp: () => {
        if (focusedBlock) {
          handleMoveUp(focusedBlock);
        }
      },
      onMoveDown: () => {
        if (focusedBlock) {
          handleMoveDown(focusedBlock);
        }
      },
      onFullscreen: () => {
        if (videoPlayerFullscreenRef.current) {
          videoPlayerFullscreenRef.current();
        }
      },
    },
    {
      enabled: true,
      skipForwardTime: CONFIG.SKIP_FORWARD_TIME,
      skipBackwardTime: CONFIG.SKIP_BACKWARD_TIME,
      fineTuneTime: CONFIG.FINE_TUNE_TIME,
    }
  );

  /**
   * Handle speaker selection from sidebar
   */
  const handleSpeakerSelection = (speakerName) => {
    if (selectedBlocks.length > 0) {
      handleBulkUpdateSpeaker(selectedBlocks, speakerName);
    }
  };

  /**
   * Handle Replace All - Open modal
   */
  const handleOpenReplaceModal = (selectedText) => {
    setReplaceSearchText(selectedText);
    setShowReplaceModal(true);
  };

  /**
   * Handle Replace All - Execute replacement
   */
  const handleExecuteReplaceAll = (searchText, replaceText) => {
    const result = handleReplaceAll(searchText, replaceText);

    if (result.success) {
      clearSelection();
      setShowReplaceModal(false);
    }

    return result;
  };

  /**
   * Open Assign to Speaker modal.
   * Captures selection data from the live DOM immediately (before the modal can invalidate it).
   */
  const handleOpenAssignSpeakerModal = () => {
    const captured = captureSelectionForAssign(selection);
    if (!captured) {
      alert('Please select some text first');
      return;
    }
    capturedAssignDataRef.current = captured;
    setShowAssignSpeakerModal(true);
  };

  /**
   * Assign captured selection to chosen speaker and close modal.
   * Uses pre-captured data so it doesn't depend on the live DOM Range.
   */
  const handleAssignSelectionToSpeakerConfirm = (speakerName) => {
    const result = handleAssignSelectionToSpeaker(capturedAssignDataRef.current, speakerName);
    if (result.success) {
      capturedAssignDataRef.current = null;
      clearSelection();
      setShowAssignSpeakerModal(false);
    } else if (result.error) {
      alert(result.error);
    }
  };

  /**
   * Initialize component
   */
  useEffect(() => {
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`te-shell flex flex-col h-screen ${className}`}>
      {/* Toolbar */}
      <Toolbar
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        onSearch={handleSearch}
        onReplace={handleReplace}
        onNextSearch={handleNextSearch}
        onPreviousSearch={handlePreviousSearch}
        searchResults={searchResults}
        currentSearchIndex={currentSearchIndex}
        onSave={manualSave}
        isSaving={isSaving}
        lastSaved={lastSaved}
        saveError={saveError}
        isDirty={isDirty}
        lockStatus={lockStatus}
        hasLock={hasLock}
        onAcquireLock={handleAcquireLock}
        onReleaseLock={handleReleaseLock}
        onForceOverride={handleForceOverride}
        lockLoading={lockLoading}
        lockInitializing={lockInitializing}
        sessionTitle={sessionTitle}
        backUrl={backUrl}
      />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Central Area - Transcript Blocks */}
        <div
          className="te-transcript-scroll flex-1 overflow-y-auto relative"
          style={{ scrollMargin: 0, scrollPadding: 0 }}
          ref={transcriptScrollParentRef}
        >
          <div
            className="te-transcript-canvas"
            ref={transcriptContainerRef}
            style={{ scrollMargin: 0, scrollPadding: 0 }}
          >
            <TranscriptBlocks
              blocks={blocks}
              speakers={speakers}
              currentBlockId={currentBlockId}
              focusedBlock={focusedBlock}
              selectedBlocks={selectedBlocks}
              onTextChange={hasLock ? handleUpdateText : () => {}}
              onSpeakerChange={hasLock ? handleUpdateSpeaker : () => {}}
              onFocusBlock={handleFocusBlock}
              onBlurBlock={handleBlurBlock}
              onSelectBlock={handleSelectBlock}
              onSplit={hasLock ? handleSplit : () => {}}
              onMergeUp={hasLock ? handleMergeUp : () => {}}
              onMergeDown={hasLock ? handleMergeDown : () => {}}
              onInsertBlock={hasLock ? handleInsertBlock : () => {}}
              onDelete={hasLock ? handleDelete : () => {}}
              onDuplicate={hasLock ? handleDuplicate : () => {}}
              onSeekToBlock={seekToBlock}
              onSeekTo={seekTo}
              onPlay={play}
              currentTime={currentTime}
              isPlaying={isPlaying}
              readOnly={!hasLock}
              scrollParentRef={transcriptScrollParentRef}
            />
          </div>
        </div>

        {/* Right Pane: Video + Speakers + Text operations */}
        <div className="te-side-pane">
          <div className="te-side-pane-scroll">
            <VideoPlayer
              ref={videoRef}
              src={videoUrl}
              isPlaying={isPlaying}
              onFullscreenToggle={videoPlayerFullscreenRef}
            />

            <div className="te-side-section te-side-section-locked relative">
              {!hasLock && lockStatus.locked && (
                <div className="te-lock-overlay">
                  <div className="te-lock-card">
                    <svg className="te-lock-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <p className="te-lock-title">Locked</p>
                    <p className="te-lock-by">by <strong>{lockStatus.locked_by_name}</strong></p>
                  </div>
                </div>
              )}
              <Sidebar
                speakers={speakers}
                onSelectSpeaker={handleSpeakerSelection}
                onAddSpeaker={handleAddSpeaker}
                onRenameSpeaker={handleRenameSpeaker}
                onDeleteSpeaker={handleDeleteSpeaker}
                onReplaceAll={handleOpenReplaceModal}
                onAssignSelectionToSpeaker={handleOpenAssignSpeakerModal}
                selectedText={selection?.text}
                selection={selection}
                className={!hasLock && lockStatus.locked ? 'pointer-events-none' : ''}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Replace All Modal */}
      <ReplaceAllModal
        isOpen={showReplaceModal}
        onClose={() => {
          setShowReplaceModal(false);
          clearSelection();
        }}
        onReplace={handleExecuteReplaceAll}
        searchText={replaceSearchText}
        occurrenceCount={countOccurrences(blocks, replaceSearchText)}
      />

      {/* Assign to Speaker Modal */}
      <AssignToSpeakerModal
        isOpen={showAssignSpeakerModal}
        onClose={() => {
          capturedAssignDataRef.current = null;
          setShowAssignSpeakerModal(false);
        }}
        speakers={speakers}
        selectedTextPreview={capturedAssignDataRef.current?.text}
        onSelectSpeaker={handleAssignSelectionToSpeakerConfirm}
      />
    </div>
  );
};

export default TranscriptEditor;

